import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const EbookView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { book } = location.state || {};
  
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1.0);

  useEffect(() => {
    if (!book) {
      navigate('/books');
    }
  }, [book, navigate]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load PDF. The file might be corrupted or unavailable.');
    setLoading(false);
  };

  const changePage = (offset) => {
    setPageNumber(prevPageNumber => prevPageNumber + offset);
  };

  const previousPage = () => {
    if (pageNumber > 1) {
      changePage(-1);
    }
  };

  const nextPage = () => {
    if (pageNumber < numPages) {
      changePage(1);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 2.5));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  if (!book) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-error">No book selected. Please go back and try again.</p>
          <button 
            onClick={() => navigate('/books')}
            className="btn btn-primary mt-4"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  // For demo purposes, if no PDF path exists, show sample PDF
  const pdfUrl = book.pdf_file_path || 'https://arxiv.org/pdf/quant-ph/0410100.pdf'; // Sample PDF for testing

  return (
    <div className="min-h-screen bg-base-200">
      {/* Reader Header */}
      <div className="bg-base-100 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Left Section - Navigation & Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/books')}
                className="btn btn-ghost btn-circle hover:bg-base-300 transition-all"
                title="Back to Library"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div className="hidden md:block">
                <h1 className="text-lg font-bold line-clamp-1">{book.title}</h1>
                <p className="text-xs text-base-content/70">by {book.author}</p>
              </div>
            </div>

            {/* Center Section - Page Controls */}
            {numPages && (
              <div className="flex items-center gap-2 bg-base-200 rounded-lg p-1">
                <button
                  onClick={previousPage}
                  disabled={pageNumber <= 1}
                  className="btn btn-sm btn-ghost disabled:opacity-30"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium">
                  Page {pageNumber} of {numPages}
                </span>
                <button
                  onClick={nextPage}
                  disabled={pageNumber >= numPages}
                  className="btn btn-sm btn-ghost disabled:opacity-30"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Right Section - Tools */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-base-200 rounded-lg p-1">
                <button
                  onClick={zoomOut}
                  className="btn btn-xs btn-ghost"
                  title="Zoom Out"
                >
                  -
                </button>
                <span className="text-xs font-mono min-w-[3rem] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={zoomIn}
                  className="btn btn-xs btn-ghost"
                  title="Zoom In"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center">
          {loading && !error && (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
              <span className="loading loading-spinner loading-lg text-primary"></span>
              <p className="text-base-content/70">Loading PDF document...</p>
            </div>
          )}

          {error && (
            <div className="card bg-error/10 border border-error/30 max-w-2xl">
              <div className="card-body text-center">
                <h3 className="text-error font-bold text-lg">Unable to Load PDF</h3>
                <p className="text-base-content/80">{error}</p>
                <div className="card-actions justify-center mt-4">
                  <button
                    onClick={() => navigate('/books')}
                    className="btn btn-ghost"
                  >
                    Back to Library
                  </button>
                </div>
              </div>
            </div>
          )}

          {!error && (
            <div className="overflow-auto max-w-full shadow-2xl rounded-lg bg-white p-4">
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex flex-col items-center justify-center p-8">
                    <span className="loading loading-spinner loading-md"></span>
                    <p className="mt-4">Loading PDF...</p>
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="shadow-lg"
                />
              </Document>
            </div>
          )}
        </div>

        {/* Page Navigation Footer (for mobile) */}
        {numPages && !error && (
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 md:hidden">
            <div className="bg-base-100 shadow-2xl rounded-full px-4 py-2 flex gap-4">
              <button
                onClick={previousPage}
                disabled={pageNumber <= 1}
                className="btn btn-circle btn-sm disabled:opacity-30"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium">
                {pageNumber} / {numPages}
              </span>
              <button
                onClick={nextPage}
                disabled={pageNumber >= numPages}
                className="btn btn-circle btn-sm disabled:opacity-30"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EbookView;